import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import * as styles from './MetricLabel.module.scss';
import ContextualHelp from '../../components/ContextualHelp';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import insightsIcon from '../../icons/non-prefilled/16x16/insights.svg';

interface WithTooltipProps {
  children: React.ReactNode;
  text?: string;
  trigger?: React.ComponentProps<typeof Tooltip>['trigger'];
}

const WithTooltip = ({ children, text, trigger }: WithTooltipProps) => (
  <Tooltip trigger={trigger} content={text} display="block">
    {children}
  </Tooltip>
);

interface MetricLabelProps {
  label: string;
  value?: string | number;
  /**  @deprecated use tooltip instead */
  legacyTooltip?: string;
  tooltip?: React.ComponentProps<typeof ContextualHelp>['content'];
  color: string;
  change?: number | null;
  startAt: Date;
  endAt: Date;
  onClick: () => void;
  enabled: boolean;
  size?: 'regular' | 'small';
}

const MetricLabel = ({
  label,
  value = 0,
  legacyTooltip,
  tooltip,
  color,
  change = null,
  startAt,
  endAt,
  onClick,
  enabled,
  size = 'regular',
}: MetricLabelProps) => {
  const startMoment = moment(startAt).utc();
  const endMoment = moment(endAt).utc();
  const days = endMoment.diff(startMoment, 'd');
  const prevStartMoment = moment(startMoment)
    .utc()
    .subtract(days + 1, 'd');
  const prevEndMoment = moment(endMoment)
    .utc()
    .subtract(days + 1, 'd');

  return (
    <>
      <WithTooltip text={legacyTooltip}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.headerButton}
            onClick={onClick}
          >
            <div
              className={styles.circle}
              style={enabled ? { backgroundColor: color } : undefined}
            />
            <div className={styles.name}>{label}</div>
          </button>
          {!!tooltip && (
            <div className={styles.contextualHelp}>
              <ContextualHelp content={tooltip} />
            </div>
          )}
        </div>
      </WithTooltip>

      <div
        className={classNames(styles.content, styles[size], {
          [styles.disabled]: !enabled,
        })}
      >
        <div className={styles.value}>{value}</div>
        {change !== null && (
          <WithTooltip
            trigger="clickOrHover"
            text={`Change in ${label.toLowerCase()} from the\nprevious period (${prevStartMoment.format(
              'MMM D',
            )} - ${prevEndMoment.format('MMM D')})\nto the selected period`}
          >
            <div
              className={classNames(styles.change, {
                [styles.disabled]: !enabled,
                [styles.up]: enabled && change >= 0,
                [styles.down]: enabled && change < 0,
              })}
            >
              <SVGIcon size={16} className={styles.arrow}>
                {change < 0 ? insightsIcon : insightsIcon}
              </SVGIcon>
              {Math.abs(change).toFixed(1)}%
            </div>
          </WithTooltip>
        )}
      </div>
    </>
  );
};

export default MetricLabel;
